/**
 * Welcome copy for the DefaultPage header, cycled on desktop like Apple’s
 * multilingual “hello”. Keep `primary` + `secondary` concise so they fit the
 * fixed desktop header height (see DefaultPage.module.scss).
 *
 * @typedef {{ bcp47: string, dir?: 'ltr' | 'rtl', primary: string, secondary: string }} HeaderTranslation
 */

/** @type {HeaderTranslation} */
export const DEFAULT_HEADER_TRANSLATION = {
  bcp47: 'en',
  primary:
    'Welcome to Good Song Club — we’re an inclusive community of music ' +
    'enthusiasts sharing good songs we’ve found on YouTube.',
  secondary:
    'We believe the best recommendations come not from cold algorithms, but warm hearts.',
};

/** @type {HeaderTranslation[]} Order: English first, then global variety. */
export const HEADER_TRANSLATIONS = [
  DEFAULT_HEADER_TRANSLATION,
  {
    bcp47: 'es',
    primary:
      'Bienvenido a Good Song Club: una comunidad inclusiva de amantes de la ' +
      'música que comparte buenas canciones encontradas en YouTube.',
    secondary:
      'Creemos que las mejores recomendaciones vienen de corazones cálidos, no de algoritmos fríos.',
  },
  {
    bcp47: 'fr',
    primary:
      'Bienvenue sur Good Song Club — une communauté inclusive de passionnés ' +
      'qui partagent de belles chansons trouvées sur YouTube.',
    secondary:
      'Les meilleures recommandations viennent de cœurs chauds, pas d’algorithmes froids.',
  },
  {
    bcp47: 'de',
    primary:
      'Willkommen beim Good Song Club — eine offene Community von Musikfans, ' +
      'die gute Songs teilen, die wir auf YouTube gefunden haben.',
    secondary:
      'Die besten Tipps kommen von warmen Herzen — nicht von kalten Algorithmen.',
  },
  {
    bcp47: 'it',
    primary:
      'Benvenuto nel Good Song Club: una community inclusiva di appassionati ' +
      'che condividono brani trovati su YouTube.',
    secondary:
      'Le migliori raccomandazioni arrivano da cuori caldi, non da algoritmi freddi.',
  },
  {
    bcp47: 'pt-BR',
    primary:
      'Bem-vindo ao Good Song Club — uma comunidade inclusiva de amantes da ' +
      'música que compartilha boas canções encontradas no YouTube.',
    secondary:
      'Acreditamos que as melhores indicações vêm de corações quentes, não de algoritmos frios.',
  },
  {
    bcp47: 'nl',
    primary:
      'Welkom bij Good Song Club — een inclusieve community van muziekliefhebbers ' +
      'die goede nummers delen die we op YouTube vonden.',
    secondary:
      'De beste aanbevelingen komen van warme harten, niet van koude algoritmen.',
  },
  {
    bcp47: 'pl',
    primary:
      'Witamy w Good Song Club — otwarta społeczność miłośników muzyki, ' +
      'która dzieli się dobrymi utworami z YouTube.',
    secondary:
      'Najlepsze rekomendacje pochodzą od ciepłych serc, nie od zimnych algorytmów.',
  },
  {
    bcp47: 'ru',
    primary:
      'Добро пожаловать в Good Song Club — открытое сообщество любителей музыки, ' +
      'которые делятся песнями с YouTube.',
    secondary:
      'Лучшие рекомендации — от тёплых сердец, а не от холодных алгоритмов.',
  },
  {
    bcp47: 'uk',
    primary:
      'Ласкаво просимо до Good Song Club — відкрита спільнота любителів музики, ' +
      'яка ділиться піснями з YouTube.',
    secondary:
      'Найкращі рекомендації — від теплих сердець, а не від холодних алгоритмів.',
  },
  {
    bcp47: 'ja',
    primary:
      'Good Song Club へようこそ。YouTube で見つけた良い曲を共有する、 ' +
      '音楽好きの包容的なコミュニティです。',
    secondary:
      '最高のおすすめは、冷たいアルゴリズムではなく、あたたかい心から生まれると信じています。',
  },
  {
    bcp47: 'ko',
    primary:
      'Good Song Club에 오신 것을 환영합니다. YouTube에서 찾은 좋은 노래를 ' +
      '나누는 포용적인 음악 애호가 모임입니다.',
    secondary:
      '최고의 추천은 차가운 알고리즘이 아니라 따뜻한 마음에서 온다고 믿습니다.',
  },
  {
    bcp47: 'zh-Hans',
    primary:
      '欢迎来到 Good Song Club：我们是一个包容的音乐爱好者社区，分享在 YouTube 上发现的好歌。',
    secondary: '我们相信最好的推荐来自温暖的心，而不是冰冷的算法。',
  },
  {
    bcp47: 'zh-Hant',
    primary:
      '歡迎來到 Good Song Club：我們是包容的音樂愛好者社群，分享在 YouTube 上發現的好歌。',
    secondary: '我們相信最好的推薦來自溫暖的心，而不是冰冷的演算法。',
  },
  {
    bcp47: 'hi',
    primary:
      'Good Song Club में आपका स्वागत है — यह YouTube पर मिले अच्छे गाने बाँटने ' +
      'वाले संगीत प्रेमियों का समावेशी समुदाय है।',
    secondary:
      'हम मानते हैं कि सबसे अच्छी सिफारिशें ठंडे एल्गोरिदम नहीं, गर्म दिलों से आती हैं।',
  },
  {
    bcp47: 'ar',
    dir: 'rtl',
    primary:
      'مرحبًا بك في Good Song Club — مجتمع موسيقي شامل يشارك أغاني رائعة وجدناها على YouTube.',
    secondary:
      'نؤمن بأن أفضل التوصيات تأتي من القلوب الدافئة لا من الخوارزميات الباردة.',
  },
  {
    bcp47: 'tr',
    primary:
      'Good Song Club’a hoş geldiniz — YouTube’da bulduğumuz iyi şarkıları paylaşan ' +
      'kapsayıcı bir müziksever topluluğuyuz.',
    secondary:
      'En iyi önerilerin soğuk algoritmalardan değil, sıcak yüreklerden geldiğine inanıyoruz.',
  },
  {
    bcp47: 'sv',
    primary:
      'Välkommen till Good Song Club — ett inkluderande community av musikälskare ' +
      'som delar bra låtar vi hittat på YouTube.',
    secondary:
      'De bästa rekommendationerna kommer från varma hjärtan, inte kalla algoritmer.',
  },
  {
    bcp47: 'nb',
    primary:
      'Velkommen til Good Song Club — et inkluderende fellesskap av musikkelskere ' +
      'som deler gode sanger vi har funnet på YouTube.',
    secondary:
      'Vi tror de beste anbefalingene kommer fra varme hjerter, ikke kalde algoritmer.',
  },
  {
    bcp47: 'da',
    primary:
      'Velkommen til Good Song Club — et inkluderende fællesskab af musikelskere, ' +
      'der deler gode sange fra YouTube.',
    secondary:
      'De bedste anbefalinger kommer fra varme hjerter — ikke kolde algoritmer.',
  },
  {
    bcp47: 'fi',
    primary:
      'Tervetuloa Good Song Clubiin — avoin yhteisö musiikin ystäville, jotka jakavat ' +
      'hyviä kappaleita YouTubesta.',
    secondary:
      'Parhaat suositukset tulevat lämpimistä sydämistä, eivät kylmistä algoritmeista.',
  },
  {
    bcp47: 'cs',
    primary:
      'Vítej v Good Song Club — otevřená komunita milovníků hudby, která sdílí ' +
      'dobré skladby z YouTube.',
    secondary:
      'Nejlepší doporučení podle nás nepocházejí z chladných algoritmů, ale ze srdce.',
  },
  {
    bcp47: 'el',
    primary:
      'Καλώς ήρθες στο Good Song Club — μια ανοιχτή κοινότητα φίλων της μουσικής ' +
      'που μοιράζεται καλά τραγούδια από το YouTube.',
    secondary:
      'Οι καλύτερες προτάσεις έρχονται από ζεστές καρδιές, όχι από κρύους αλγόριθμους.',
  },
  {
    bcp47: 'th',
    primary:
      'ยินดีต้อนรับสู่ Good Song Club — ชุมชนคนรักเพลงที่แบ่งปันเพลงดีจาก YouTube',
    secondary:
      'เราเชื่อว่าคำแนะนำที่ดีที่สุดมาจากหัวใจที่อบอุ่น ไม่ใช่จากอัลกอริทึมที่เย็นชา',
  },
  {
    bcp47: 'vi',
    primary:
      'Chào mừng đến với Good Song Club — cộng đồng yêu nhạc cởi mở, chia sẻ ' +
      'những bài hát hay tìm thấy trên YouTube.',
    secondary:
      'Chúng tôi tin những gợi ý hay nhất đến từ trái tim ấm, không phải thuật toán lạnh.',
  },
  {
    bcp47: 'id',
    primary:
      'Selamat datang di Good Song Club — komunitas pecinta musik yang berbagi ' +
      'lagu-lagu bagus dari YouTube.',
    secondary:
      'Rekomendasi terbaik menurut kami datang dari hati yang hangat, bukan algoritme dingin.',
  },
  {
    bcp47: 'fil',
    primary:
      'Maligayang pagdating sa Good Song Club — isang inclusive na komunidad ng ' +
      'mahilig sa musika na nagbabahagi ng magagandang kanta mula sa YouTube.',
    secondary:
      'Naniniwala kaming ang pinakamahusay na rekomendasyon ay mula sa mainit na puso, hindi sa malamig na algorithm.',
  },
  {
    bcp47: 'sw',
    primary:
      'Karibu Good Song Club — jamii ya wenye mapenzi ya muziki inayoshirikiana ' +
      'nyimbo nzuri kutoka YouTube.',
    secondary:
      'Tunaamini mapendekezo mazuri zaidi yanatoka mioyo ya joto, siyo algorithimu baridi.',
  },
  {
    bcp47: 'fa',
    dir: 'rtl',
    primary:
      'به Good Song Club خوش آمدید — جامعه‌ای فراگیر از علاقه‌مندان موسیقی که آهنگ‌های خوب از یوتیوب را به اشتراک می‌گذارند.',
    secondary:
      'بهترین پیشنهادها از دل‌های گرم می‌آیند، نه از الگوریتم‌های سرد.',
  },
  {
    bcp47: 'bn',
    primary:
      'Good Song Club-এ স্বাগতম — YouTube থেকে পাওয়া ভালো গান শেয়ার করা সঙ্গীতপ্রেমীদের একটি উন্মুক্ত সমাজ।',
    secondary:
      'আমরা বিশ্বাস করি সেরা সুপারিশ আসে উষ্ণ হৃদয় থেকে, ঠান্ডা অ্যালগরিদম থেকে নয়।',
  },
  {
    bcp47: 'ta',
    primary:
      'Good Song Club-க்கு வரவேற்கிறோம் — YouTube-ல் கண்ட நல்ல பாடல்களைப் பகிரும் இசை ரசிகர்களின் திறந்த சமூகம்.',
    secondary:
      'சிறந்த பரிந்துரைகள் குளிர்ந்த வழிமுறைகளிலிருந்து அல்ல; வெப்பமான இதயங்களிலிருந்து வருகின்றன என நம்புகிறோம்.',
  },
  {
    bcp47: 'ro',
    primary:
      'Bun venit la Good Song Club — o comunitate deschisă de iubitori de muzică ' +
      'care împărtășește piese găsite pe YouTube.',
    secondary:
      'Cele mai bune recomandări vin din inimi calde, nu din algoritmi reci.',
  },
  {
    bcp47: 'hu',
    primary:
      'Üdv a Good Song Clubban — egy befogadó közösség zenerajongóknak, akik jó ' +
      'számokat osztanak meg a YouTube-ról.',
    secondary:
      'A legjobb ajánlások szerintünk nem hideg algoritmusoktól, hanem meleg szívektől jönnek.',
  },
  {
    bcp47: 'ca',
    primary:
      'Benvingut al Good Song Club — una comunitat inclusiva d’aficionats a la música ' +
      'que comparteix bones cançons trobades a YouTube.',
    secondary:
      'Les millors recomanacions venen de cors càlids, no d’algoritmes freds.',
  },
  {
    bcp47: 'ga',
    primary:
      'Fáilte go Good Song Club — pobal cuimsitheach de lucht grá ceoil a roinneann ' +
      'ceol maith a fuair muid ar YouTube.',
    secondary:
      'Creidimid gurb iad na moltaí is fearr a thagann ó chroíthe te, ní ó algartaim fhuara.',
  },
  {
    bcp47: 'lv',
    primary:
      'Laipni lūdzam Good Song Club — atvērta kopiena mūzikas mīļotājiem, kas dalās ' +
      'ar labām dziesmām no YouTube.',
    secondary:
      'Labākās rekomendācijas nāk no sirdīm siltām, nevis no aukstiem algoritmiem.',
  },
  {
    bcp47: 'sk',
    primary:
      'Vitaj v Good Song Club — otvorená komunita milovníkov hudby, ktorá zdieľa ' +
      'dobré skladby z YouTube.',
    secondary:
      'Najlepšie odporúčania podľa nás neprichádzajú z chladných algoritmov, ale zo srdca.',
  },
  {
    bcp47: 'bg',
    primary:
      'Добре дошли в Good Song Club — отворена общност от любители на музиката, ' +
      'които споделят добри песни от YouTube.',
    secondary:
      'Най-добрите препоръки идват от топли сърца, не от студени алгоритми.',
  },
  {
    bcp47: 'sr',
    primary:
      'Dobro došli u Good Song Club — otvorena zajednica ljubitelja muzike koja deli ' +
      'dobre pesme sa YouTube-a.',
    secondary:
      'Najbolje preporuke dolaze od toplih srca, ne od hladnih algoritama.',
  },
];
